import {
  DatePicker,
  DatePickerProps,
  DateValidationError,
  PickerChangeHandlerContext,
  PickersSectionListRef
} from '@mui/x-date-pickers-pro';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getUTCDate, getUTCRunDate } from '../../../utils/helpers';
import { CustomActionBar } from './CustomActionBar';
import { useSnackbar } from '../../../Context/SnackbarContext';

type CustomDatePickerProps = Omit<DatePickerProps, 'onChange' | 'value'> & {
  value: string | number | boolean | null | Date;
  onChange: (value: Date | null) => void;
  onClose?: () => void;
  isMonthYear?: boolean;
  formatDate?: string;
  error?: boolean;
  errorId?: string;
};

export function CustomDatePicker({
  value,
  onChange,
  closeOnSelect = false,
  onClose,
  isMonthYear = false,
  formatDate,
  error: hasError,
  errorId,
  ...props
}: CustomDatePickerProps) {
  const [tempDate, setTempDate] = useState<Date | null>(null);
  const tempDateRef = useRef<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const lastAcceptedValueRef = useRef<Date | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const sectionListRef = useRef<PickersSectionListRef>(null);
  const [dateFieldFocused, setDateFieldFocused] = useState(false);
  const dateFieldFocusedRef = useRef(dateFieldFocused);
  const wasAccepted = useRef(false);
  const ignoreBlurRef = useRef(false);
  const { setSnack } = useSnackbar();
  const format = formatDate ?? (isMonthYear ? 'MM/yyyy' : 'MM/dd/yyyy');

  const updateTempDate = useCallback((date: Date | null) => {
    tempDateRef.current = date;
    setTempDate(date);
  }, []);

  useEffect(() => {
    const sections = sectionListRef.current?.getRoot().querySelectorAll('[role="spinbutton"]') ?? [];
    sections.forEach((section) => {
      if (hasError && errorId) section.setAttribute('aria-describedby', errorId);
      else section.removeAttribute('aria-describedby');
    });
  }, [hasError, errorId, tempDate]);

  const isYearTooBig = (date: Date | null) => {
    if (!date || isNaN(date.getTime())) return false;
    const isValid = date.getUTCFullYear() < 2200;
    if (isValid) return false;
    setError('Date must be earlier than 2200');
    return true;
  };

  useEffect(() => {
    if (error && !dateFieldFocusedRef.current) {
      setSnack({ message: error, type: 'error', open: true, duration: 5000 });
      setError(null);
    }
  }, [error]);

  useEffect(() => {
    dateFieldFocusedRef.current = dateFieldFocused;
  }, [dateFieldFocused]);

  useEffect(() => {
    // A null/empty value must stay null — String(null) is the literal text "null",
    // which `new Date()` can't parse and turns into an Invalid Date, rendering the picker
    // with a permanent error border.
    const val =
      value === null || value === ''
        ? null
        : typeof value === 'number'
          ? isMonthYear
            ? getUTCRunDate(new Date(value))
            : getUTCDate(new Date(value))
          : typeof value === 'string' || value instanceof Date
            ? isMonthYear
              ? getUTCRunDate(value)
              : getUTCDate(value)
            : null;
    updateTempDate(val);
    lastAcceptedValueRef.current = val;
  }, [value, isMonthYear, updateTempDate]);

  const handleTempChange = useCallback(
    (newValue: Date | null, _context?: PickerChangeHandlerContext<DateValidationError>) => {
      updateTempDate(newValue);
    },
    [updateTempDate]
  );

  const handleDateFieldFocus = () => {
    setDateFieldFocused(true);
  };

  const isInsideField = (target: EventTarget | null) =>
    target instanceof Node && (inputRef.current === target || !!sectionListRef.current?.getRoot().contains(target));

  const isFieldActive = () => isInsideField(document.activeElement);

  const closeEditMode = useCallback(() => {
    onClose && onClose();
  }, [onClose]);

  const commitPendingDate = (closeWhenUnchanged: boolean) => {
    if (dateFieldFocusedRef.current) return false;

    const pendingDate = tempDateRef.current;
    if (isYearTooBig(pendingDate)) {
      updateTempDate(lastAcceptedValueRef.current);
      return true;
    }
    if (pendingDate?.toString() !== lastAcceptedValueRef.current?.toString()) {
      onChange(pendingDate);
      lastAcceptedValueRef.current = pendingDate;
      return true;
    }
    if (closeWhenUnchanged) closeEditMode();
    return false;
  };

  const commitAfterFieldLeft = (skipWhileFieldActive: boolean) => {
    const handled = commitPendingDate(false);
    requestAnimationFrame(() => {
      if (skipWhileFieldActive && isFieldActive()) return;
      commitPendingDate(!handled);
    });
  };

  const handleDateFieldBlur = (event: React.FocusEvent<HTMLElement>) => {
    if (isInsideField(event.relatedTarget)) return;

    dateFieldFocusedRef.current = false;
    setDateFieldFocused(false);
    if (ignoreBlurRef.current) {
      ignoreBlurRef.current = false;
      return;
    }

    commitAfterFieldLeft(true);
  };

  const handleAccept = useCallback(
    (newValue: Date | null) => {
      if (dateFieldFocusedRef.current) return;
      wasAccepted.current = true;
      if (isYearTooBig(newValue)) {
        updateTempDate(lastAcceptedValueRef.current);
        return;
      }
      if (newValue?.toString() !== lastAcceptedValueRef.current?.toString()) {
        onChange(newValue);
        lastAcceptedValueRef.current = newValue;
      } else {
        closeEditMode();
      }
    },
    [closeEditMode, onChange, updateTempDate]
  );

  const handleClose = () => {
    if (wasAccepted.current) {
      wasAccepted.current = false;
      return;
    }
    commitPendingDate(true);
  };

  const handleEnterPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      dateFieldFocusedRef.current = false;
      setDateFieldFocused(false);
      commitAfterFieldLeft(false);
    }
    if (e.key === 'Escape') {
      // Reset tempDate to last accepted value
      updateTempDate(lastAcceptedValueRef.current);
      closeEditMode();
    }
  };

  return (
    <DatePicker
      {...props}
      value={tempDate}
      onChange={handleTempChange}
      onAccept={handleAccept}
      onClose={handleClose}
      views={isMonthYear ? ['month', 'year'] : undefined}
      format={format}
      closeOnSelect={closeOnSelect}
      slots={{
        actionBar: isMonthYear ? CustomActionBar : undefined
      }}
      slotProps={{
        actionBar: { actions: isMonthYear ? ['next', 'accept', 'cancel'] : ['today', 'accept', 'cancel'] },
        textField: {
          variant: 'outlined',
          size: 'small',
          inputRef,
          sectionListRef,
          ...(hasError && { error: true }),
          ...(hasError && errorId && { slotProps: { input: { 'aria-describedby': errorId } } }),
          onKeyDown: handleEnterPress,
          onFocus: handleDateFieldFocus,
          onBlur: handleDateFieldBlur
        },
        openPickerButton: {
          onMouseDown: () => {
            ignoreBlurRef.current = isFieldActive();
          }
        }
      }}
    />
  );
}
